import React, { useState } from 'react';
import { submitIndividualRegistration } from '../lib/dataService';
import { toast } from 'sonner';
import { Send, User, Users, ShieldCheck, Trophy } from 'lucide-react';
import './registration.css';

export const DISTRICTS = [
  'George Town', 'West Bay', 'Bodden Town', 'East End', 'North Side',
  'Cayman Brac', 'Little Cayman',
];

export default function IndividualRegistrationForm({ leagueLabel, divisions = [], pageSlug = '' }) {
  const [form, setForm] = useState({
    league: divisions.length === 1 ? divisions[0] : '',
    child_name: '', child_dob: '', child_school: '', district: '', medical_conditions: '',
    parent1_name: '', parent1_email: '', parent1_phone: '',
    parent2_name: '', parent2_email: '', parent2_phone: '',
  });
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.league) { toast.error('Please select a league division.'); return; }
    if (!form.child_name.trim()) { toast.error("Please enter the child's full name."); return; }
    if (!form.child_dob.trim()) { toast.error("Please enter the child's date of birth."); return; }
    if (!form.child_school.trim()) { toast.error("Please enter the child's school."); return; }
    if (!form.district) { toast.error('Please select your district.'); return; }
    if (!form.parent1_name.trim() || !form.parent1_email.trim() || !form.parent1_phone.trim()) {
      toast.error("Parent 1's name, email and phone number are required."); return;
    }
    if (!consent) { toast.error('Please accept the disclaimer and photo consent.'); return; }
    setSending(true);
    try {
      await submitIndividualRegistration({ ...form, consent, page_slug: pageSlug });
      setSubmitted(true);
      toast.success('Registration submitted successfully!');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to submit registration. Please try again.');
    }
    setSending(false);
  };

  if (submitted) {
    return (
      <div className="reg-form-wrap">
        <div className="reg-success" data-testid="individual-reg-success">
          <ShieldCheck size={44} />
          <h3>Registration Received!</h3>
          <p><strong>{form.child_name}</strong> is now registered for the <strong>{form.league}</strong>.</p>
          <p>A confirmation email has been sent to <strong>{form.parent1_email}</strong>. Our league coordinators will place {form.child_name} on a team and contact you with the team details and schedule.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="reg-form-wrap" id="register-individual">
      <div className="reg-header">
        <h2>Individual Player Registration</h2>
        <p>
          Don't have a team or club? Sign up your child below and our league coordinators will place them on a
          {' '}{leagueLabel} team. Fields marked <span className="reg-req">*</span> are required.
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <section className="reg-card">
          <h3><Trophy size={19} /> League Division</h3>
          <div className="reg-grid reg-grid-2">
            <label>Division <span className="reg-req">*</span>
              <select className="reg-input" value={form.league} onChange={set('league')} required data-testid="individual-reg-division">
                <option value="">Select a division</option>
                {divisions.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </label>
          </div>
        </section>

        <section className="reg-card">
          <h3><User size={19} /> Child's Information</h3>
          <div className="reg-grid reg-grid-3">
            <label>Full Name <span className="reg-req">*</span>
              <input type="text" className="reg-input" value={form.child_name} onChange={set('child_name')} required data-testid="individual-reg-child-name" />
            </label>
            <label>Date of Birth <span className="reg-req">*</span>
              <input type="date" className="reg-input" value={form.child_dob} onChange={set('child_dob')} required data-testid="individual-reg-child-dob" />
            </label>
            <label>School <span className="reg-req">*</span>
              <input type="text" className="reg-input" value={form.child_school} onChange={set('child_school')} required data-testid="individual-reg-child-school" />
            </label>
            <label>District <span className="reg-req">*</span>
              <select className="reg-input" value={form.district} onChange={set('district')} required data-testid="individual-reg-district">
                <option value="">Select your district</option>
                {DISTRICTS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </label>
          </div>
          <div className="reg-grid" style={{ marginTop: 16 }}>
            <label>Medical conditions we should be aware of
              <textarea
                className="reg-input"
                rows={3}
                value={form.medical_conditions}
                onChange={set('medical_conditions')}
                placeholder="E.g. asthma, allergies, epilepsy — write 'None' if not applicable"
                data-testid="individual-reg-medical"
              />
            </label>
            <p className="reg-confidential-note">
              This information will be kept strictly confidential and will help our coaches to be better
              equipped in their interactions with your child.
            </p>
          </div>
        </section>

        <section className="reg-card">
          <h3><Users size={19} /> Parent / Guardian Information</h3>
          <div className="reg-official">
            <h4>Parent / Guardian 1</h4>
            <div className="reg-grid reg-grid-3">
              <label>Full Name <span className="reg-req">*</span>
                <input type="text" className="reg-input" value={form.parent1_name} onChange={set('parent1_name')} required data-testid="individual-reg-parent1-name" />
              </label>
              <label>Email <span className="reg-req">*</span>
                <input type="email" className="reg-input" value={form.parent1_email} onChange={set('parent1_email')} required data-testid="individual-reg-parent1-email" />
              </label>
              <label>Phone <span className="reg-req">*</span>
                <input type="tel" className="reg-input" value={form.parent1_phone} onChange={set('parent1_phone')} required data-testid="individual-reg-parent1-phone" />
              </label>
            </div>
          </div>
          <div className="reg-official">
            <h4>Parent / Guardian 2 <span className="reg-optional">(optional)</span></h4>
            <div className="reg-grid reg-grid-3">
              <label>Full Name
                <input type="text" className="reg-input" value={form.parent2_name} onChange={set('parent2_name')} />
              </label>
              <label>Email
                <input type="email" className="reg-input" value={form.parent2_email} onChange={set('parent2_email')} />
              </label>
              <label>Phone
                <input type="tel" className="reg-input" value={form.parent2_phone} onChange={set('parent2_phone')} />
              </label>
            </div>
          </div>
        </section>

        <section className="reg-card">
          <h3><ShieldCheck size={19} /> Disclaimer & Photo Consent</h3>
          <label className="reg-declaration">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="individual-reg-consent" />
            <span>
              I understand that participation in basketball activities involves a risk of injury, and I consent to my
              child's participation in the {leagueLabel}. I confirm that the information provided is accurate, and I
              authorize CIBA staff to seek emergency medical treatment for my child if needed. I also consent to
              photographs and videos taken during league games being used by CIBA for promotional purposes.
              I agree to receive league notices by email. <span className="reg-req">*</span>
            </span>
          </label>
        </section>

        <button type="submit" className="btn-primary reg-submit" disabled={sending} data-testid="individual-reg-submit">
          <Send size={16} /> {sending ? 'Submitting...' : 'Register Player'}
        </button>
      </form>
    </div>
  );
}
